import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Bell,
  MessageSquare,
  Plus,
  Send,
  CheckCheck,
  Trash2,
  Search,
  Filter,
  Mail,
  MailOpen,
  Clock,
  XCircle,
  AlertCircle,
  CreditCard,
  FileText,
  User,
  ChevronLeft,
  LogOut,
} from "lucide-react";
import authService from "@/services/authService";
import inboxService from "@/services/inboxService";
import InboxBell from "@/components/shared/InboxBell";
import ThemeToggle from "@/components/shared/ThemeToggle";
import { toast } from "sonner";

const CATEGORY_ICONS = {
  GENERAL: MessageSquare,
  PAYMENT: CreditCard,
  SUBMISSION: FileText,
  QC: AlertCircle,
  ASSIGNMENT: User,
  SYSTEM: Bell,
};

const PRIORITY_COLORS = {
  LOW: "bg-slate-100 text-slate-600 border-slate-200",
  NORMAL: "bg-[#fff2d8] text-[#9a6500] border-[#eadfcd]",
  HIGH: "bg-[#fff7e8] text-[#b77900] border-[#e4c06f]",
  CRITICAL: "bg-red-50 text-red-700 border-red-200",
};

const STATUS_COLORS = {
  OPEN: "bg-green-100 text-green-700",
  CLOSED: "bg-slate-100 text-slate-600",
  WAITING_RESPONSE: "bg-yellow-100 text-yellow-700",
};

const goldButtonClass =
  "bg-[#c98905] text-white shadow-[0_10px_24px_rgba(178,116,0,0.22)] hover:bg-[#a96d00]";
const goldOutlineClass =
  "border-[#d8bd7a] bg-white text-[#8a5b00] hover:bg-[#fff8eb]";
const goldInputClass =
  "border-[#eadfcd] bg-white/90 text-slate-900 placeholder:text-slate-400 focus-visible:ring-[#d4a63a]";

function InboxHeader({ onNavigate, onLogout }) {
  const navItems = [
    { label: "Dashboard", path: "/dashboard" },
    { label: "Participate", path: "/participate" },
    { label: "Publisher", path: "/publisher/workflow" },
    { label: "Narrator", path: "/narrator" },
    { label: "Work", path: "/work" },
    { label: "Inbox", path: "/inbox", active: true },
  ];

  return (
    <header className="sticky top-0 z-30 border-b border-[#eadfcd] bg-white/95 backdrop-blur dark:border-[#2f260f] dark:bg-[#070705]/95">
      <div className="mx-auto flex h-[102px] max-w-[1640px] items-center justify-between px-6">
        <button
          type="button"
          onClick={() => onNavigate("/dashboard")}
          className="flex items-center"
          aria-label="Go to dashboard"
        >
          <img
            src="/images/golden-records-real-logo.png"
            alt="Golden Records"
            className="h-16 w-56 rounded-lg object-cover object-center"
          />
        </button>

        <nav className="hidden items-center gap-9 lg:flex">
          {navItems.map((item) => (
            <button
              key={item.label}
              type="button"
              onClick={() => onNavigate(item.path)}
              className={`relative py-2 text-lg font-medium transition-colors ${
                item.active
                  ? "text-[#9a6500] dark:text-[#f2c95d]"
                  : "text-slate-900 hover:text-[#9a6500] dark:text-[#eee3cf] dark:hover:text-[#f2c95d]"
              }`}
            >
              {item.label}
              {item.active && (
                <span className="absolute inset-x-0 -bottom-1 h-[2px] rounded-full bg-[#c98905]" />
              )}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-5">
          <ThemeToggle />
          <InboxBell
            className="relative rounded-full p-3 text-[#9a6500] hover:bg-[#fff2d8] hover:text-[#7a5200]"
            iconClassName="h-7 w-7"
          />
          <Button
            type="button"
            variant="outline"
            onClick={onLogout}
            className={`h-12 gap-3 px-6 text-lg ${goldOutlineClass}`}
          >
            <LogOut className="h-5 w-5" />
            Logout
          </Button>
        </div>
      </div>
    </header>
  );
}

function GoldPageBackground() {
  return (
    <>
      <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_83%_13%,rgba(245,207,117,0.23),transparent_25%),linear-gradient(180deg,#ffffff_0%,#fffaf2_40%,#fff0d3_100%)] dark:bg-[radial-gradient(circle_at_82%_10%,rgba(224,165,27,0.30),transparent_22%),linear-gradient(180deg,#050504_0%,#090805_48%,#030302_100%)]" />
      <div className="pointer-events-none fixed inset-x-0 top-[120px] -z-10 h-[58vh] opacity-70 [background:repeating-radial-gradient(ellipse_at_center,rgba(224,165,27,0.18)_0_1px,transparent_1px_18px)] dark:opacity-35" />
      <div className="pointer-events-none fixed inset-x-0 bottom-0 -z-10 h-[42vh] bg-gradient-to-t from-[#fff0d3] via-[#fff7e8]/70 to-transparent dark:from-black dark:via-[#0b0905]/70" />
    </>
  );
}

export default function Inbox() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const messageEndRef = useRef(null);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("notifications");

  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [notificationFilter, setNotificationFilter] = useState("all");
  const [notificationCategory, setNotificationCategory] = useState("ALL");

  // Threads state
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [threadFilter, setThreadFilter] = useState("all");

  // New thread modal
  const [showNewThreadModal, setShowNewThreadModal] = useState(false);
  const [newThreadData, setNewThreadData] = useState({
    thread_type: "SUPPORT",
    subject: "",
    initial_message: "",
    priority: "NORMAL",
  });

  // Load user on mount
  useEffect(() => {
    loadUser();
  }, []);

  // Check URL params for thread selection
  useEffect(() => {
    const threadId = searchParams.get("thread");
    if (threadId && threads.length > 0) {
      const thread = threads.find((t) => t.id === threadId);
      if (thread) {
        handleSelectThread(thread);
        setActiveTab("messages");
      }
    }
  }, [searchParams, threads]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [threadMessages]);

  const loadUser = async () => {
    try {
      if (!authService.isAuthenticated()) {
        navigate("/login");
        return;
      }
      const userData = await authService.getMe();
      setUser(userData);
      await Promise.all([loadNotifications(), loadThreads()]);
    } catch (error) {
      console.error("Error loading user:", error);
      toast.error("Session expired");
      navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  const loadNotifications = async () => {
    try {
      const params = {};
      if (notificationFilter === "unread") params.is_read = false;
      if (notificationFilter === "read") params.is_read = true;
      if (notificationCategory && notificationCategory !== "ALL") params.category = notificationCategory;

      const data = await inboxService.getNotifications(params);
      setNotifications(data.notifications);
    } catch (error) {
      console.error("Error loading notifications:", error);
    }
  };

  const loadThreads = async () => {
    try {
      const params = {};
      if (threadFilter !== "all") params.status = threadFilter.toUpperCase();

      const data = await inboxService.getThreads(params);
      setThreads(data);
    } catch (error) {
      console.error("Error loading threads:", error);
    }
  };

  const handleLogout = async () => {
    await authService.logout();
    navigate("/login");
  };

  // Notification handlers
  const handleMarkAsRead = async (notification) => {
    if (notification.is_read) return;
    try {
      await inboxService.markNotificationsRead([notification.id]);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n))
      );
    } catch (error) {
      toast.error("Failed to mark as read");
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await inboxService.markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      toast.success("All notifications marked as read");
    } catch (error) {
      toast.error("Failed to mark all as read");
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await inboxService.deleteNotification(notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      toast.success("Notification deleted");
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleNotificationClick = (notification) => {
    handleMarkAsRead(notification);
    if (notification.action_url) {
      navigate(notification.action_url);
    }
  };

  // Thread handlers
  const handleSelectThread = async (thread) => {
    setSelectedThread(thread);
    setSearchParams({ thread: thread.id });
    try {
      const data = await inboxService.getThread(thread.id);
      setThreadMessages(data.messages);
      // Update thread unread count locally
      setThreads((prev) =>
        prev.map((t) => (t.id === thread.id ? { ...t, unread_count: 0 } : t))
      );
    } catch (error) {
      toast.error("Failed to load messages");
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedThread) return;

    setSendingMessage(true);
    try {
      await inboxService.addMessage(selectedThread.id, newMessage.trim());
      setNewMessage("");
      // Reload messages
      const data = await inboxService.getThread(selectedThread.id);
      setThreadMessages(data.messages);
      // Update thread last_message_at
      setThreads((prev) =>
        prev.map((t) =>
          t.id === selectedThread.id
            ? { ...t, last_message_at: new Date().toISOString() }
            : t
        )
      );
      toast.success("Message sent");
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleCreateThread = async () => {
    if (!newThreadData.subject.trim() || !newThreadData.initial_message.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const result = await inboxService.createThread(newThreadData);
      toast.success("Thread created");
      setShowNewThreadModal(false);
      setNewThreadData({
        thread_type: "SUPPORT",
        subject: "",
        initial_message: "",
        priority: "NORMAL",
      });
      await loadThreads();
      // Select the new thread
      const newThreads = await inboxService.getThreads({});
      const created = newThreads.find((t) => t.id === result.id);
      if (created) handleSelectThread(created);
    } catch (error) {
      toast.error("Failed to create thread");
    }
  };

  const handleCloseThread = async () => {
    if (!selectedThread) return;
    try {
      await inboxService.closeThread(selectedThread.id);
      toast.success("Thread closed");
      setSelectedThread(null);
      setSearchParams({});
      await loadThreads();
    } catch (error) {
      toast.error("Failed to close thread");
    }
  };

  // Reload data when filters change
  useEffect(() => {
    if (user) loadNotifications();
  }, [notificationFilter, notificationCategory]);

  useEffect(() => {
    if (user) loadThreads();
  }, [threadFilter]);

  if (loading) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#fffaf2]">
        <GoldPageBackground />
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-[#b77900]"></div>
          <p className="mt-4 text-[#6f5b35]">Loading inbox...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#fffaf2] dark:bg-[#050504]" data-testid="inbox-page">
      <GoldPageBackground />

      <InboxHeader onNavigate={navigate} onLogout={handleLogout} />

      <main className="relative z-10 mx-auto max-w-[1640px] px-6 py-12 md:px-8">

        {/* Page Title */}
        <div className="mb-10 flex items-center justify-between">
          <div>
            <h1
              className="mb-5 text-6xl font-bold text-[#7a5200] dark:text-[#f2c95d]"
              style={{ fontFamily: "Playfair Display, serif" }}
              
            >
              Inbox
            </h1>
            <p className="text-xl text-slate-600 dark:text-[#d5c8ae]">
              Manage your notifications and messages
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-10 h-auto gap-0 rounded-md border border-[#d8bd7a] bg-white/85 p-0 shadow-sm">
            <TabsTrigger
              value="notifications"
              className="flex h-14 items-center gap-3 rounded-md border border-transparent px-7 text-lg text-[#8a5b00] data-[state=active]:border-[#c98905] data-[state=active]:bg-white data-[state=active]:text-[#7a5200] data-[state=active]:shadow-none"
            >
              <Bell className="w-4 h-4" />
              Notifications
              {notifications.filter((n) => !n.is_read).length > 0 && (
                <Badge className="bg-[#c98905] text-white text-xs px-1.5 py-0">
                  {notifications.filter((n) => !n.is_read).length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="messages"
              className="flex h-14 items-center gap-3 rounded-md border border-transparent px-7 text-lg text-[#8a5b00] data-[state=active]:border-[#c98905] data-[state=active]:bg-white data-[state=active]:text-[#7a5200] data-[state=active]:shadow-none"
            >
              <MessageSquare className="w-4 h-4" />
              Messages
              {threads.reduce((acc, t) => acc + (t.unread_count || 0), 0) > 0 && (
                <Badge className="bg-[#c98905] text-white text-xs px-1.5 py-0">
                  {threads.reduce((acc, t) => acc + (t.unread_count || 0), 0)}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* NOTIFICATIONS TAB */}
          <TabsContent value="notifications">
            <Card className="min-h-[380px] rounded-xl border border-[#eadfcd] bg-white/95 shadow-[0_24px_70px_rgba(82,55,12,0.10)] dark:border-[#4d3d16] dark:bg-[#0d0c09]/95 dark:shadow-[0_24px_70px_rgba(0,0,0,0.45)]">

              <CardHeader className="px-10 pt-10 pb-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <CardTitle
                    className="text-3xl text-[#080d1f] dark:text-[#f8efe0]"
                    style={{ fontFamily: "Playfair Display, serif" }}
                  >
                    All Notifications
                  </CardTitle>
                  <div className="flex flex-wrap items-center gap-4">
                    <Select value={notificationFilter} onValueChange={setNotificationFilter}>
                      <SelectTrigger className={`h-12 w-40 ${goldInputClass}`}>
                        <SelectValue placeholder="Filter" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="unread">Unread</SelectItem>
                        <SelectItem value="read">Read</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={notificationCategory} onValueChange={setNotificationCategory}>
                      <SelectTrigger className={`h-12 w-52 ${goldInputClass}`}>
                        <SelectValue placeholder="Category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">All Categories</SelectItem>
                        <SelectItem value="GENERAL">General</SelectItem>
                        <SelectItem value="PAYMENT">Payment</SelectItem>
                        <SelectItem value="SUBMISSION">Submission</SelectItem>
                        <SelectItem value="QC">QC</SelectItem>
                        <SelectItem value="ASSIGNMENT">Assignment</SelectItem>
                        <SelectItem value="SYSTEM">System</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleMarkAllRead}
                      className={`h-12 px-5 text-base ${goldOutlineClass}`}
                      data-testid="mark-all-read"
                    >
                      <CheckCheck className="w-4 h-4 mr-1" />
                      Mark all read
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-10 pb-10">
                {notifications.length === 0 ? (
                  <div className="flex min-h-[250px] flex-col items-center justify-center text-center">
                    <div className="mb-5 flex h-28 w-28 items-center justify-center rounded-full bg-[#fff2d8] text-[#c98905]">
                      <Bell className="h-16 w-16" />
                    </div>
                    <p
                      className="text-2xl font-semibold text-[#080d1f]"
                      style={{ fontFamily: "Playfair Display, serif" }}
                    >
                      No notifications found
                    </p>
                    <p className="mt-3 text-xl text-slate-500">You&apos;re all caught up!</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {notifications.map((notification) => {
                      const Icon = CATEGORY_ICONS[notification.category] || Bell;
                      return (
                        <div
                          key={notification.id}
                          className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                            !notification.is_read
                              ? "bg-[#fff8eb] border-[#e4c06f]"
                              : "bg-white border-[#eadfcd] hover:bg-[#fffaf2]"
                          }`}
                          onClick={() => handleNotificationClick(notification)}
                          data-testid={`notification-item-${notification.id}`}
                        >
                          <div className="flex gap-4">
                            <div
                              className={`p-2 rounded-lg ${
                                PRIORITY_COLORS[notification.priority]
                              }`}
                            >
                              <Icon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <p
                                    className={`font-medium ${
                                      !notification.is_read
                                        ? "text-slate-900"
                                        : "text-slate-700"
                                    }`}
                                  >
                                    {notification.title}
                                  </p>
                                  <p className="text-sm text-slate-500 mt-0.5">
                                    {notification.body}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge
                                    variant="outline"
                                    className={`text-xs ${PRIORITY_COLORS[notification.priority]}`}
                                  >
                                    {notification.priority}
                                  </Badge>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteNotification(notification.id);
                                    }}
                                    className="p-1 text-slate-400 hover:text-red-500"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                              <div className="flex items-center gap-4 mt-2">
                                <span className="text-xs text-slate-400">
                                  {new Date(notification.created_at).toLocaleString()}
                                </span>
                                {notification.category && (
                                  <Badge variant="outline" className="text-xs">
                                    {notification.category}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* MESSAGES TAB */}
          <TabsContent value="messages">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Thread List */}
              <Card className="lg:col-span-1 rounded-xl border border-[#eadfcd] bg-white/95 shadow-[0_24px_70px_rgba(82,55,12,0.10)]">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle
                      className="text-2xl text-[#080d1f]"
                      style={{ fontFamily: "Playfair Display, serif" }}
                    >
                      Threads
                    </CardTitle>
                    <Button
                      size="sm"
                      onClick={() => setShowNewThreadModal(true)}
                      className={goldButtonClass}
                      data-testid="new-thread-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      New
                    </Button>
                  </div>
                  <Select value={threadFilter} onValueChange={setThreadFilter}>
                    <SelectTrigger className={`mt-2 ${goldInputClass}`}>
                      <SelectValue placeholder="Filter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Threads</SelectItem>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </CardHeader>
                <CardContent className="max-h-[500px] overflow-y-auto">
                  {threads.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-[#fff2d8] text-[#c98905]">
                        <MessageSquare className="h-9 w-9" />
                      </div>
                      <p className="text-slate-500 text-sm">No threads yet</p>
                      <Button
                        variant="link"
                        className="mt-2 text-[#9a6500] hover:text-[#7a5200]"
                        onClick={() => setShowNewThreadModal(true)}
                      >
                        Start a conversation
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {threads.map((thread) => (
                        <div
                          key={thread.id}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedThread?.id === thread.id
                              ? "bg-[#fff2d8] border-[#d8bd7a]"
                              : thread.unread_count > 0
                              ? "bg-[#fff8eb] border-[#e4c06f]"
                              : "bg-white border-[#eadfcd] hover:bg-[#fffaf2]"
                          }`}
                          onClick={() => handleSelectThread(thread)}
                          data-testid={`thread-${thread.id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                {thread.unread_count > 0 ? (
                                  <Mail className="w-4 h-4 text-[#c98905]" />
                                ) : (
                                  <MailOpen className="w-4 h-4 text-slate-400" />
                                )}
                                <p className="font-medium text-slate-900 truncate">
                                  {thread.subject}
                                </p>
                              </div>
                              <p className="text-xs text-slate-500 mt-1 truncate">
                                {thread.thread_type}
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <Badge className={STATUS_COLORS[thread.status] + " text-xs"}>
                                {thread.status}
                              </Badge>
                              {thread.unread_count > 0 && (
                                <Badge className="bg-[#c98905] text-white text-xs">
                                  {thread.unread_count} new
                                </Badge>
                              )}
                            </div>
                          </div>
                          <p className="text-xs text-slate-400 mt-2">
                            {new Date(thread.last_message_at).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Message View */}
              <Card className="lg:col-span-2 rounded-xl border border-[#eadfcd] bg-white/95 shadow-[0_24px_70px_rgba(82,55,12,0.10)]">
                {selectedThread ? (
                  <>
                    <CardHeader className="pb-3 border-b border-[#eadfcd]">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="lg:hidden"
                            onClick={() => {
                              setSelectedThread(null);
                              setSearchParams({});
                            }}
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </Button>
                          <div>
                            <CardTitle
                              className="text-2xl text-[#080d1f]"
                              style={{ fontFamily: "Playfair Display, serif" }}
                            >
                              {selectedThread.subject}
                            </CardTitle>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge className={STATUS_COLORS[selectedThread.status] + " text-xs"}>
                                {selectedThread.status}
                              </Badge>
                              <Badge variant="outline" className="text-xs">
                                {selectedThread.thread_type}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        {selectedThread.status !== "CLOSED" && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCloseThread}
                            className={goldOutlineClass}
                            data-testid="close-thread-btn"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Close
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      {/* Messages */}
                      <div className="max-h-[350px] overflow-y-auto p-4 space-y-4">
                        {threadMessages.map((message) => {
                          const isCurrentUser = message.sender_user_id === user?.id;
                          return (
                            <div
                              key={message.id}
                              className={`flex ${isCurrentUser ? "justify-end" : "justify-start"}`}
                            >
                              <div
                                className={`max-w-[80%] rounded-lg p-3 ${
                                  isCurrentUser
                                    ? "bg-[#c98905] text-white"
                                    : "bg-[#fff8eb] text-slate-900"
                                }`}
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <span
                                    className={`text-xs font-medium ${
                                      isCurrentUser ? "text-[#fff2d8]" : "text-slate-500"
                                    }`}
                                  >
                                    {message.sender_name}
                                  </span>
                                  <Badge
                                    className={`text-[10px] ${
                                      isCurrentUser
                                        ? "bg-white/20 text-[#fff8eb]"
                                        : "bg-slate-200 text-slate-600"
                                    }`}
                                  >
                                    {message.sender_role}
                                  </Badge>
                                </div>
                                <p className="text-sm whitespace-pre-wrap">{message.body}</p>
                                <p
                                  className={`text-[10px] mt-1 ${
                                    isCurrentUser ? "text-[#fff2d8]" : "text-slate-400"
                                  }`}
                                >
                                  {new Date(message.created_at).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                        <div ref={messageEndRef} />
                      </div>

                      {/* Reply Box */}
                      {selectedThread.status !== "CLOSED" && (
                        <div className="p-4 border-t border-[#eadfcd] bg-[#fffaf2]">
                          <div className="flex gap-2">
                            <Textarea
                              placeholder="Type your message..."
                              value={newMessage}
                              onChange={(e) => setNewMessage(e.target.value)}
                              className={`min-h-[80px] ${goldInputClass}`}
                              data-testid="message-input"
                            />
                          </div>
                          <div className="flex justify-end mt-2">
                            <Button
                              onClick={handleSendMessage}
                              disabled={!newMessage.trim() || sendingMessage}
                              className={goldButtonClass}
                              data-testid="send-message-btn"
                            >
                              <Send className="w-4 h-4 mr-1" />
                              {sendingMessage ? "Sending..." : "Send"}
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </>
                ) : (
                  <CardContent className="flex items-center justify-center h-[500px]">
                    <div className="text-center">
                      <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-[#fff2d8] text-[#c98905]">
                        <MessageSquare className="h-14 w-14" />
                      </div>
                      <p className="text-slate-500">Select a thread to view messages</p>
                    </div>
                  </CardContent>
                )}
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* New Thread Modal */}
      <Dialog open={showNewThreadModal} onOpenChange={setShowNewThreadModal}>
        <DialogContent className="border border-[#eadfcd] bg-[#fffaf2] shadow-[0_24px_70px_rgba(82,55,12,0.16)]">
          <DialogHeader>
            <DialogTitle
              className="text-2xl text-[#080d1f]"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              Start New Thread
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Type</label>
              <Select
                value={newThreadData.thread_type}
                onValueChange={(v) => setNewThreadData({ ...newThreadData, thread_type: v })}
              >
                <SelectTrigger className={goldInputClass}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SUPPORT">Support Request</SelectItem>
                  <SelectItem value="PROJECT">Project Discussion</SelectItem>
                  <SelectItem value="ADMIN">Admin Inquiry</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Subject *</label>
              <Input
                placeholder="Enter subject..."
                value={newThreadData.subject}
                onChange={(e) =>
                  setNewThreadData({ ...newThreadData, subject: e.target.value })
                }
                className={goldInputClass}
                data-testid="new-thread-subject"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Priority</label>
              <Select
                value={newThreadData.priority}
                onValueChange={(v) => setNewThreadData({ ...newThreadData, priority: v })}
              >
                <SelectTrigger className={goldInputClass}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LOW">Low</SelectItem>
                  <SelectItem value="NORMAL">Normal</SelectItem>
                  <SelectItem value="HIGH">High</SelectItem>
                  <SelectItem value="CRITICAL">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Message *</label>
              <Textarea
                placeholder="Describe your request..."
                value={newThreadData.initial_message}
                onChange={(e) =>
                  setNewThreadData({ ...newThreadData, initial_message: e.target.value })
                }
                className={`min-h-[100px] ${goldInputClass}`}
                data-testid="new-thread-message"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowNewThreadModal(false)}
              className={goldOutlineClass}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateThread}
              className={goldButtonClass}
              data-testid="create-thread-btn"
            >
              Create Thread
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
